/* The spreadsheet round trip, as four steps. A list rather than a table: a diff row is a heading, a
   sentence and a few field changes, and a table of those is a list with lines drawn on it.
   Tokens only (ADR-0007). */

/* ----------------------------------------------------------------------------- the stepper */

.stepper {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  margin: 0;
  padding: 0;
  list-style: none;
}

.step {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  background: var(--panel);
  color: var(--ink-2);
}

.step[data-state="now"] {
  border-color: var(--accent);
  background: var(--accent-w);
  color: var(--ink);
}

.step[data-state="skipped"] {
  background: var(--panel-2);
  color: var(--muted);
}

/* The number or the tick. Decoration: the label and the word beside it are the text. */
.stepMark {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel);
  font-size: var(--t-min);
  font-weight: 700;
  font-variant-numeric: lining-nums tabular-nums;
}

.step[data-state="now"] .stepMark {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--on-fill);
}

.step[data-state="done"] .stepMark {
  border-color: var(--ok-line);
  background: var(--ok-w);
  color: var(--ok);
}

.stepLabel {
  min-width: 0;
  font-size: var(--t-dense);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.stepState {
  margin-left: auto;
  font-size: var(--t-min);
  color: var(--muted);
  white-space: nowrap;
}

@media (width <= 900px) {
  .stepper {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 480px) {
  .stepper {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ------------------------------------------------------------------------------ step 1 */

.upload {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: var(--gap);
}

.uploadBlock {
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  background: var(--panel-2);
}

.blockTitle {
  margin: 0 0 6px;
  font-size: var(--t-dense);
  font-weight: 650;
  color: var(--ink);
}

.uploadBlock input[type="file"] {
  max-width: 100%;
  font: inherit;
  font-size: var(--t-dense);
}

@media (width <= 900px) {
  .upload {
    grid-template-columns: minmax(0, 1fr);
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 10px 0;
}

/* ------------------------------------------------------------------------------ step 3 */

/* One tile per disposition, number first and its word after. The word is what says it; the tint is
   the second signal. */
.counts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  margin: 0 0 12px;
}

.count {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  background: var(--panel);
}

/* A conflict is not an error — somebody corrected a reading, which is the system working. Warn,
   never critical. */
.countWarn {
  border-color: var(--warn-line);
  background: var(--warn-w);
}

.countCrit {
  border-color: var(--crit-line);
  background: var(--crit-w);
}

.countN {
  font-size: var(--t-figure);
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
}

.countLabel {
  font-size: var(--t-min);
  color: var(--ink-2);
}

.absent,
.sensitive {
  margin: 0 0 10px;
  padding: 8px 10px;
  border: 1px solid var(--line-2);
  border-left-width: 3px;
  border-radius: 7px;
  background: var(--panel-2);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.sensitive {
  border-color: var(--warn-line);
  background: var(--warn-w);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin: 12px 0 6px;
}

.filename {
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--h-ctl);
  padding: 0 11px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel);
  font-size: var(--t-dense);
  color: var(--ink);
  white-space: nowrap;
  cursor: pointer;
}

.toggle:hover {
  border-color: var(--accent-line);
  background: var(--panel-2);
}

.toggle:has(input:checked) {
  border-color: var(--accent);
  background: var(--accent-w);
}

.toggle:has(input:focus-visible) {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  padding: 10px 0;
  border-top: 1px solid var(--line);
}

.row:first-child {
  border-top: 0;
}

.rowHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tag {
  display: inline-block;
  padding: 1px 7px;
  border: 1px solid var(--accent-line);
  border-radius: var(--radius-pill);
  background: var(--accent-w);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
  white-space: nowrap;
}

.tag::first-letter {
  text-transform: uppercase;
}

.tagWarn {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.tagCrit {
  border-color: var(--crit-line);
  background: var(--crit-w);
  color: var(--crit);
}

.tagQuiet {
  border-color: var(--line-2);
  background: var(--panel-2);
  color: var(--muted);
}

.rowWhere {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.rowWhy {
  display: block;
  margin-top: 2px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.changes {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.changes li {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr) 16px minmax(0, 1fr);
  align-items: baseline;
  gap: 8px;
  padding: 2px 0;
  font-size: var(--t-min);
}

@media (width <= 560px) {
  .changes li {
    grid-template-columns: 90px minmax(0, 1fr) 14px minmax(0, 1fr);
    gap: 4px;
  }
}

.field {
  color: var(--muted);
}

/* The old value is present and quiet; the new one is the claim. Neither is hidden — a diff with
   one value in it is not a diff. */
.was {
  color: var(--ink-2);
  text-decoration: line-through;
  overflow-wrap: anywhere;
}

.arrow {
  color: var(--muted);
}

.now {
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.pending {
  margin: 14px 0 0;
  padding: 8px 10px;
  border: 1px dashed var(--line-2);
  border-radius: 7px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* **A role that cannot edit, told so instead of shown a control** (UX-04).
   `--warn`, not `--crit`: a Viewer meeting a permission boundary is the product working. Red would
   say they had done something wrong, and the next thing they do is open a support ticket. */
.unauthorized {
  margin: 10px 0 0;
  padding: 9px 11px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* What the preview did **not** check. Shares `.pending`'s dashed border on purpose: both say "this
   is a limit of the product rather than a fact about your file", and a reader who has learned what
   that border means on one of them has learned it on the other. */
.unchecked {
  margin: 14px 0 0;
  padding: 8px 10px;
  border: 1px dashed var(--line-2);
  border-radius: 7px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* ------------------------------------------------------------------------------ step 4 */

/* Step four is its own panel now, so it needs no rule above it; the class is kept for the markup. */
.apply {
  margin: 0;
}

/* What is still owed before the button can be used: one pill per kind, a tick when met. */
.checklist {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 0 0 10px;
  padding: 0;
  list-style: none;
}

/* The confirmation. `--warn-w` rather than plain, because the step between asking and doing is the
   only moment a reader can still stop — but not `--crit`, which would say something had gone
   wrong when nothing has. */
.confirm {
  padding: 10px 12px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.confirm p {
  margin: 0 0 8px;
}

.confirm p:last-child {
  margin-bottom: 0;
}

/* What was applied. Quiet: the act is over, and the counts are the content. */
.applied {
  margin: 0;
}
